import discoverMessages from '@/lang/messages/discover';
import { displayLocaleDateString, getUtcDateForMDY } from '@/utils/date-time';
import { GuideMetadataCustomContentType } from '@/utils/guides';
import { MediaGuideMetadata } from '@/utils/media/types';
import { PropMindshiftMindshiftWeekTypeEnum } from '@calm/evexia-analytics';
import { useActiveCirclePacks } from '@rhg/hooks/use-packs';
import commonMessages from '@rhg/lang/messages/common';
import { getGuideId } from '@rhg/utils/media';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

const isMindshift = (session: MediaGuideMetadata) => {
    return (
        session?.metadata?.custom_content_type ===
        GuideMetadataCustomContentType.Mindshift
    );
};

/**
 * Do not use this hook outside of SessionCard and its dependencies.
 */
export function useMindshiftSessionCardContext(
    session: MediaGuideMetadata,
    packClass?: string
) {
    const { formatMessage } = useIntl();
    const isMindshiftSession = useMemo(() => isMindshift(session), [session]);
    const { packs, isFetched: isPacksFetched } = useActiveCirclePacks(
        isMindshiftSession && packClass ? [packClass] : []
    );
    const isFetched = isPacksFetched || !isMindshiftSession;
    const pack = packs?.[0];

    const packPosition = useMemo(
        () =>
            pack?.items?.findIndex(
                (item) =>
                    item.type === 'guide' &&
                    item.guideId === getGuideId(session)
            ),
        [pack?.items, session]
    );

    const { mindshiftCategoryTitle, mindshiftWeekType } = useMemo(() => {
        if (!isFetched) {
            return {
                mindshiftCategoryTitle: '',
                mindshiftWeekType: '',
            };
        }

        if (isMindshiftSession && session?.metadata?.guide_published_at) {
            if (packPosition === 0) {
                return {
                    mindshiftCategoryTitle: formatMessage(
                        commonMessages['common.this_weeks'],
                        {
                            weekly: formatMessage(discoverMessages.mindshift),
                        }
                    ),
                    mindshiftWeekType: PropMindshiftMindshiftWeekTypeEnum.This,
                };
            }
            if (packPosition === 1) {
                return {
                    mindshiftCategoryTitle: formatMessage(
                        commonMessages['common.last_weeks'],
                        {
                            weekly: formatMessage(discoverMessages.mindshift),
                        }
                    ),
                    mindshiftWeekType: PropMindshiftMindshiftWeekTypeEnum.Last,
                };
            }
            const rawGuidePublishedAt: unknown =
                session.metadata?.guide_published_at;
            const guidePublishedAt =
                typeof rawGuidePublishedAt === 'string'
                    ? rawGuidePublishedAt
                    : undefined;
            const publishedDate = guidePublishedAt
                ? getUtcDateForMDY(guidePublishedAt.split('T')[0])
                : null;

            return {
                mindshiftCategoryTitle:
                    publishedDate && !isNaN(publishedDate.getTime())
                        ? displayLocaleDateString(publishedDate)
                        : '',
                mindshiftWeekType: PropMindshiftMindshiftWeekTypeEnum.All,
            };
        }

        return {
            mindshiftCategoryTitle: session?.contentType,
            mindshiftWeekType: undefined,
        };
    }, [
        formatMessage,
        isFetched,
        isMindshiftSession,
        packPosition,
        session?.contentType,
        session.metadata?.guide_published_at,
    ]);

    const trackProps = {
        ...(mindshiftWeekType
            ? {
                  MindshiftWeekType:
                      mindshiftWeekType as PropMindshiftMindshiftWeekTypeEnum,
              }
            : {}),
    };

    return {
        isMindshiftSession,
        isMindshiftContextFetched: isFetched,
        mindshiftCategoryTitle,
        trackProps,
    };
}
